import React, { ChangeEvent, useState, useEffect, ChangeEventHandler } from 'react'

import { MdCancel } from 'react-icons/md'
import ImagePDF from '../../../assets/images/pdf-image.png'
import Alert from '../../../helpers/Alert'
import ModalVisualization from '../../ModalVisualization'
import ImageEditor from '../../ImageEditor'
import { createFile, getAllFileByQuery, deleteFile, updateFile, getFileById, handleImage } from '../../../services/FileService'
import './styles.css'

interface typeProps {
  name: string;
  label: string;
  osId: string,
  etapa: string,
  required?: boolean;
}

interface typeList {
  id: string,
  url: string,
  origin: string,
  file?: File,
  label?: string,
  ordem?: number | null
}

export default function UploadFiles({
  name,
  osId,
  etapa,
  label,
  required
}: typeProps) {

  const [dataServer, setDataServer] = useState<typeList[]>([])
  const [data, setdata] = useState<typeList[]>([])


  const [salvando, setSalvando] = useState(false)

  /*
   * Recarrega a lista a partir do servidor.
   *
   * Virou funcao (antes vivia so dentro do useEffect) porque agora tambem roda
   * DEPOIS de salvar. Era esse o motivo de o usuario precisar sair da O.S. e
   * voltar: ao salvar, os itens eram marcados como "ja estao no servidor" mas
   * mantinham o id local (`dataModificacao-nomeDoArquivo`), que nao existe no
   * banco. Na gravacao seguinte o sistema pedia esse id ao servidor, o Mongo
   * recusava (nao e um identificador valido) e caia no erro generico.
   * Recarregando, a tela passa a trabalhar com os ids reais.
   */
  const recarregar = () =>
    getAllFileByQuery(osId, etapa).then((res: any) => {
      const newData = res.map((i: any) => {
        return {
          id: i.id,
          url: `${process.env.REACT_APP_API_URL}${i.url}`,
          origin: 'server',
          label: i.label,
          ordem: i.ordem,
        }
      })
      setDataServer(newData);
      setdata(newData);
      return newData;
    })

  // busca imagens já salvas no bd
  useEffect(() => {
    recarregar()
  }, [osId, etapa])
  // captura imagem inseridas pelo usario e guarda localmente
  const setLocalFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = await handleImage(e, data);
    if (files) {
      setdata(files);
    }
  }
  // remove files localmente
  const removeFile = (url: string) => {
    const newData = data.filter((i: typeList) => i.url !== url)
    setdata(newData)
  }
  const handleAddLegend2Image = (item: typeList, label: string) => {
    const newData = data.map(dataItem => {
      if (dataItem.id === item.id) { dataItem.label = label }
      return dataItem
    })
    setdata(newData)
  }
  // salvar files
  const saveData = async (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (salvando) return; // evita duplo clique criando o arquivo duas vezes
    setSalvando(true);

    try {
      // 1. Remover arquivos excluídos localmente do servidor
      const arrayIdLocal = data.map((i) => i.id);
      const filesToDelete = dataServer.filter((i) => !arrayIdLocal.includes(i.id));

      for (const fileToDelete of filesToDelete) {
        await deleteFile(fileToDelete.id);
      }

      /*
       * 2 e 3. Grava na ORDEM DA LISTA, um de cada vez.
       *
       * Antes eram dois `Promise.all` (paralelos). Como o relato e o PDF
       * ordenavam pela hora da gravacao, a sequencia exibida virava a ordem em
       * que a rede respondia -- dai as imagens sairem como 1, 4, 2, 3.
       *
       * Agora a posicao vai gravada no campo `ordem`, e o laco em serie percorre
       * a lista na ordem em que ela esta na tela. O indice `posicao` e a fonte da
       * verdade da sequencia.
       */
      for (let posicao = 0; posicao < data.length; posicao++) {
        const item = data[posicao];

        if (item.origin === 'local') {
          if (item.file) {
            await createFile(item.file.name, osId, etapa, item.file, item.label, posicao);
          }
          continue;
        }

        /*
         * Ja esta no servidor: so regrava se legenda ou posicao mudaram.
         * Regravar todos a cada save era o que reembaralhava imagens que ja
         * estavam certas -- e fazia uma imagem antiga "pular" para o fim do
         * relatorio quando alguem so corrigia a legenda dela.
         */
        const original = dataServer.find((i) => i.id === item.id);
        const mudouLegenda = (original ? original.label : undefined) !== item.label;
        const mudouPosicao = (original ? original.ordem : undefined) !== posicao;
        if (!original || mudouLegenda || mudouPosicao) {
          const { data: arquivo } = await getFileById(item.id);
          await updateFile(item.id, arquivo.nome, arquivo.os, arquivo.etapa, item.label, posicao);
        }
      }

      // Volta a ler do servidor: a tela passa a ter os ids reais e o usuário
      // pode continuar anexando na mesma sessão, sem sair e voltar da O.S.
      await recarregar();

      Alert(true, 'Arquivos salvos com sucesso!');
    } catch (error: any) {
      console.error(error);
      // Mensagem util em vez de "erro inesperado": foi por causa dela que o
      // problema chegou ao suporte sem nenhuma pista do que havia falhado.
      const detalhe =
        error && error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Verifique sua conexão e tente novamente.';
      Alert(false, `Não foi possível salvar os arquivos. ${detalhe}`);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <div className="field">
        <label htmlFor="label">{label}:</label>
        <input type="file"
          onClick={(e: any) => (e.target.value = null)}
          name={name}
          accept='image/png, image/jpeg, image/jpg, application/pdf'
          multiple
          onChange={async e => await setLocalFiles(e)}
          required={required}
        />
      </div>
      <div className="field">
        <div id='list-files'>
          {
            /*
             * Ordem de inserção, sem reordenar.
             *
             * Havia aqui um `.sort()` por id. Dois problemas: o id de um arquivo
             * recém-anexado é `dataDeModificacao-nomeDoArquivo`, então a lista
             * saía na ordem da data do arquivo no disco — não na ordem em que a
             * pessoa selecionou. E `.sort()` reordena o próprio array do estado,
             * o que o React não espera e deixa a lista instável ao redesenhar.
             */
            data?.map((item: typeList) =>
              <div key={item.id}>
                <span className='item' >
                  <MdCancel className='item-icon' onClick={() => { removeFile(item.url) }} />
                  <ModalVisualization arquivo={item} />
                  {(
                    item.file?.name.includes('.pdf') ||
                    item.url.includes('.pdf')
                  ) ?
                    <img src={ImagePDF} />
                    :
                    <img src={item.url} />
                  }
                  <input value={item.label} style={{ width: "100%" }} type="text" placeholder="Digite uma legenda" onChange={(event) => handleAddLegend2Image(item, event.target.value)} />
                </span>

              </div>
            )

          }
        </div>
      </div>
      <div className="field">
        <button className='btn btn-secundary w-100' disabled={salvando} onClick={(e: any) => { saveData(e) }}>
          {salvando ? 'Salvando…' : 'Salvar arquivos'}
        </button>
      </div>
    </>
  )
}
